import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, Star, MapPin, ShoppingBag, ArrowLeft, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/api";
import DishCard from "@/components/DishCard";
import DishOptionsModal from "@/components/DishOptionsModal";
import ReviewsList from "@/components/ReviewsList";
import { cuisineLabel } from "@/utils/cuisines";
import { useCart } from "@/contexts/CartContext";
import { formatGNF, imgUrl } from "@/utils/format";
import { fmtReopen } from "@/utils/reopen";
import { toast } from "sonner";
import { BrandLoader } from "@/components/BrandLoader";

export default function RestaurantDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [restaurant, setRestaurant] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [responseRate, setResponseRate] = useState(0);
  const [repliesCount, setRepliesCount] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const { addItem, cart, count, subtotal } = useCart();
  const [customizeDish, setCustomizeDish] = useState(null);
  const sectionRefs = useRef({});
  const navRef = useRef(null);
  const spyLocked = useRef(false);

  const REVIEWS_PAGE = 10;

  const fetchReviewsPage = async (offset = 0, append = false) => {
    setReviewsLoading(true);
    try {
      const { data } = await apiClient.get(`/restaurants/${id}/reviews`, { params: { limit: REVIEWS_PAGE, offset } });
      setReviewsTotal(data.total);
      setResponseRate(data.response_rate || 0);
      setRepliesCount(data.replies_count || 0);
      setReviews((prev) => append ? [...prev, ...data.items] : data.items);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    apiClient.get(`/restaurants/${id}`).then(({ data }) => setRestaurant(data)).catch(() => {});
    fetchReviewsPage(0, false).catch(() => {});
  }, [id, i18n.language]);

  const dishesByCategory = useMemo(() => {
    if (!restaurant?.dishes) return {};
    const grouped = {};
    restaurant.dishes.forEach((d) => {
      if (!grouped[d.category]) grouped[d.category] = [];
      grouped[d.category].push(d);
    });
    return grouped;
  }, [restaurant]);

  const categories = Object.keys(dishesByCategory);

  // Scrollspy : détermine la section visible pendant le défilement
  useEffect(() => {
    const keys = [...categories, "reviews"];
    const onScroll = () => {
      if (spyLocked.current) return;
      const offset = 150; // header (64) + barre sticky (~60) + marge
      let current = keys[0] || null;
      for (const k of keys) {
        const el = sectionRefs.current[k];
        if (el && el.getBoundingClientRect().top <= offset) current = k;
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.join("|")]);

  // Garde la pastille active visible dans la barre sticky
  useEffect(() => {
    if (!activeSection || !navRef.current) return;
    const btn = navRef.current.querySelector(`[data-nav="${activeSection}"]`);
    btn?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeSection]);

  const scrollToSection = (k) => {
    const el = sectionRefs.current[k];
    if (!el) return;
    spyLocked.current = true;
    setActiveSection(k);
    const top = el.getBoundingClientRect().top + window.scrollY - 135;
    window.scrollTo({ top, behavior: "smooth" });
    setTimeout(() => { spyLocked.current = false; }, 700);
  };

  if (!restaurant) {
    return <BrandLoader message="Chargement du restaurant…" />;
  }

  const handleAdd = (dish) => {
    // Plat avec ingrédients modifiables ou cuisson → panneau de personnalisation
    const customizable = dish.ingredients?.some((i) => i.type !== "fixed") || dish.cooking_choices?.length;
    if (customizable) {
      setCustomizeDish(dish);
      return;
    }
    addItem(restaurant, dish);
    toast.success(`${dish.name} ajouté au panier`);
  };

  const catLabel = (c) => {
    const key = `categories.${c}`;
    return t(key) === key ? c : t(key);
  };

  return (
    <div>
      <div className="relative h-64 sm:h-80 overflow-hidden bg-elevated">
        {restaurant.cover_image ? (
          <img src={imgUrl(restaurant.cover_image)} alt={restaurant.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FFF5F6] via-[#FFE1D6] to-[#FFD8CC]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <Link to="/restaurants" className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-full p-2 hover:bg-white">
          <ArrowLeft className="w-4 h-4" />
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="bg-white rounded-3xl border border-sand p-6 shadow-xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="flex items-start gap-4">
              {restaurant.logo_image && (
                <img
                  src={imgUrl(restaurant.logo_image)}
                  alt={`${restaurant.name} logo`}
                  data-testid="restaurant-detail-logo"
                  className="w-20 h-20 rounded-2xl object-cover border-2 border-white shadow-lg -mt-14 shrink-0"
                />
              )}
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-brand-red">{restaurant.zone}</div>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-1">{restaurant.name}</h1>
                {(restaurant.cuisines || []).length > 0 && (
                  <p className="text-sm text-mute mt-1 font-medium" data-testid="restaurant-cuisines">
                    {restaurant.cuisines.map((c) => cuisineLabel(c, i18n.language)).join(" · ")}
                  </p>
                )}
                {(restaurant.specialties || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="restaurant-specialties">
                    {restaurant.specialties.map((s) => (
                      <span key={s} className="rounded-full bg-elevated border border-sand px-2.5 py-1 text-xs font-semibold" data-testid={`specialty-badge-${s}`}>
                        {cuisineLabel(s, i18n.language)}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-mute mt-1">{restaurant.tagline}</p>
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-mute">
                  <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#F4C430] text-[#F4C430]" /> {restaurant.rating}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {restaurant.delivery_time_min} min</span>
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {restaurant.address}</span>
                  <span>{t("restaurantDetail.delivery")} {formatGNF(restaurant.delivery_fee)}</span>
                  <span>{t("restaurantDetail.min")} {formatGNF(restaurant.min_order)}</span>
                  {repliesCount > 0 && (
                    <span data-testid="response-rate-chip" className="inline-flex items-center gap-1.5 bg-[#E7F6EE] text-brand-green rounded-full px-3 py-0.5 font-semibold">
                      <MessageSquare className="w-3.5 h-3.5" /> {t("reviews.responseRate", { rate: responseRate })}
                    </span>
                  )}
                </div>
                {restaurant.chain_branches?.length > 0 && (
                  <div className="mt-2 text-xs text-mute" data-testid="chain-branches">
                    Aussi disponible à :{" "}
                    {restaurant.chain_branches.map((b, i) => (
                      <React.Fragment key={b.restaurant_id}>
                        {i > 0 && " · "}
                        <Link to={`/restaurants/${b.restaurant_id}`} data-testid={`chain-branch-${b.restaurant_id}`} className="underline hover:text-brand-red">{b.zone}</Link>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              {restaurant.open ? (
                <span className="bg-[#E7F6EE] text-brand-green rounded-full px-4 py-2 text-sm font-semibold">{t("restaurantDetail.openNow")}</span>
              ) : (
                <span className="bg-elevated text-mute rounded-full px-4 py-2 text-sm font-semibold" data-testid="restaurant-closed-badge">
                  {restaurant.next_opening_at
                    ? t("card.closedUntil", { when: fmtReopen(restaurant.next_opening_at, i18n.language) })
                    : t("restaurantDetail.closed")}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            {/* Barre de navigation sticky (scrollspy) — style Deliveroo */}
            <div className="sticky top-16 z-30 -mx-4 px-4 py-3 bg-[#FDFBF7]/95 backdrop-blur border-b border-sand mb-6">
              <div ref={navRef} className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="menu-scrollspy-nav">
                {categories.map((c) => (
                  <button
                    key={c}
                    data-nav={c}
                    onClick={() => scrollToSection(c)}
                    data-testid={`menu-cat-${c}`}
                    className={`rounded-full px-4 py-2 text-sm font-medium border capitalize whitespace-nowrap transition-colors ${activeSection === c ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-ink"}`}
                  >{catLabel(c)}</button>
                ))}
                <button
                  data-nav="reviews"
                  onClick={() => scrollToSection("reviews")}
                  data-testid="menu-cat-reviews"
                  className={`rounded-full px-4 py-2 text-sm font-medium border whitespace-nowrap transition-colors ${activeSection === "reviews" ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-ink"}`}
                >{t("restaurantDetail.reviewsTitle")}</button>
              </div>
            </div>

            {categories.map((c) => (
              <section
                key={c}
                ref={(el) => { sectionRefs.current[c] = el; }}
                data-testid={`menu-section-${c}`}
                className="mb-10"
              >
                <h2 className="font-display text-2xl font-semibold mb-4 capitalize">{catLabel(c)}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dishesByCategory[c].map((d) => (
                    <DishCard key={d.dish_id} dish={d} onAdd={handleAdd} />
                  ))}
                </div>
              </section>
            ))}

            <section className="mb-10" id="reviews" ref={(el) => { sectionRefs.current["reviews"] = el; }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-brand-red" /> {t("restaurantDetail.reviewsTitle")}</h2>
                {reviewsTotal > 0 && (
                  <span className="text-sm text-mute">{t("restaurantDetail.reviewsCount", { count: reviewsTotal })} · ⭐ {restaurant.rating}</span>
                )}
              </div>
              <ReviewsList reviews={reviews} />
              {reviews.length < reviewsTotal && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => fetchReviewsPage(reviews.length, true)}
                    disabled={reviewsLoading}
                    data-testid="load-more-reviews"
                    className="rounded-full border border-sand bg-white hover:bg-elevated px-6 py-3 text-sm font-semibold disabled:opacity-60"
                  >
                    {reviewsLoading ? t("restaurantDetail.loadingReviews") : t("restaurantDetail.loadMoreReviews", { n: reviewsTotal - reviews.length })}
                  </button>
                </div>
              )}
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 bg-white rounded-2xl border border-sand p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <ShoppingBag className="w-5 h-5 text-brand-red" />
                <h3 className="font-display text-xl font-semibold">{t("restaurantDetail.yourCart")}</h3>
              </div>
              {count === 0 ? (
                <p className="text-mute text-sm">{t("restaurantDetail.cartEmptyInline")}</p>
              ) : (
                <div className="space-y-3">
                  {cart.items.map((i) => (
                    <div key={i.line_id} className="flex justify-between text-sm gap-2">
                      <span className="min-w-0">
                        {i.quantity}× {i.name}
                        {i.selected_options?.length > 0 && (
                          <span className="block text-[11px] text-mute truncate">{i.selected_options.map((o) => o.name).join(", ")}</span>
                        )}
                      </span>
                      <span className="font-medium shrink-0">{formatGNF((i.price + (i.options_total || 0)) * i.quantity)}</span>
                    </div>
                  ))}
                  <div className="border-t border-sand pt-3 flex justify-between text-sm">
                    <span className="text-mute">{t("restaurantDetail.subtotal")}</span>
                    <span className="font-semibold">{formatGNF(subtotal)}</span>
                  </div>
                  <Link
                    to="/cart"
                    data-testid="view-cart-btn"
                    className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white py-3 font-semibold"
                  >{t("restaurantDetail.viewCart", { count })}</Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      <DishOptionsModal
        open={!!customizeDish}
        dish={customizeDish}
        onClose={() => setCustomizeDish(null)}
        onConfirm={(custom) => {
          addItem(restaurant, customizeDish, custom);
          toast.success(`${customizeDish.name} ajouté au panier`);
          setCustomizeDish(null);
        }}
      />
    </div>
  );
}
